import { NODE_KIND } from 'app/services/formula/visualBuilderModel/visualBuilderModel.constants';
import _ from 'lodash';

import { getUUID } from 'utils/uuid';

import {
  EQUALITY_OPERATOR,
  FIELD_PATH_RE,
  LINT_CODE,
  LINT_SEVERITY,
  SUSPICIOUS_LITERAL_RE,
} from './ruleLinter.constants';

// ---------------------------------------------------------------------------
// Finding constructor
// ---------------------------------------------------------------------------

/**
 * @param {{ nodeId: string|null, severity: string, code: string, message: string, detail: string }} params
 * @returns {{ id: string, nodeId: string|null, severity: string, code: string, message: string, detail: string }}
 */
const makeFinding = ({ nodeId, severity, code, message, detail }) => ({
  id: getUUID(),
  nodeId,
  severity,
  code,
  message,
  detail,
});

// ---------------------------------------------------------------------------
// Tree walking helpers (pure)
// ---------------------------------------------------------------------------

/**
 * Flatten a group's descendants into the list of comparison nodes reachable
 * through nested AND groups only. OR groups and collection predicates open a
 * new logical scope, so they are NOT flattened into the parent AND — a value
 * forced inside one OR branch does not contradict a sibling branch.
 *
 * @param {Object} group
 * @returns {Array<Object>}
 */
const flattenAndComparisons = group => {
  const children = group.children ?? [];

  return children.reduce((acc, child) => {
    if (child.kind === NODE_KIND.COMPARISON) {
      return [...acc, child];
    }

    if (child.kind === NODE_KIND.GROUP && child.op === 'AND') {
      return [...acc, ...flattenAndComparisons(child)];
    }

    return acc;
  }, []);
};

/**
 * Collect every group node in the tree (root + nested groups + collection
 * predicate groups) so per-group analyzers run at each logical scope.
 *
 * @param {Object} node
 * @returns {Array<Object>}
 */
const collectGroups = node => {
  if (_.isNil(node)) {
    return [];
  }

  if (node.kind === NODE_KIND.GROUP) {
    const nested = (node.children ?? []).flatMap(collectGroups);

    return [node, ...nested];
  }

  if (node.kind === NODE_KIND.COLLECTION) {
    return collectGroups(node.predicate);
  }

  if (node.kind === NODE_KIND.SWITCH) {
    return (node.cases ?? []).flatMap(caseItem => collectGroups(caseItem.condition));
  }

  return [];
};

/**
 * Collect every comparison node in the tree, regardless of grouping.
 *
 * @param {Object} node
 * @returns {Array<Object>}
 */
const collectComparisons = node => {
  if (_.isNil(node)) {
    return [];
  }

  if (node.kind === NODE_KIND.COMPARISON) {
    return [node];
  }

  if (node.kind === NODE_KIND.GROUP) {
    return (node.children ?? []).flatMap(collectComparisons);
  }

  if (node.kind === NODE_KIND.COLLECTION) {
    return collectComparisons(node.predicate);
  }

  if (node.kind === NODE_KIND.SWITCH) {
    return (node.cases ?? []).flatMap(caseItem => collectComparisons(caseItem.condition));
  }

  return [];
};

const collectCollections = node => {
  if (_.isNil(node)) {
    return [];
  }

  if (node.kind === NODE_KIND.COLLECTION) {
    return [node, ...collectCollections(node.predicate)];
  }

  if (node.kind === NODE_KIND.GROUP) {
    return (node.children ?? []).flatMap(collectCollections);
  }

  if (node.kind === NODE_KIND.SWITCH) {
    return (node.cases ?? []).flatMap(caseItem => collectCollections(caseItem.condition));
  }

  return [];
};

/**
 * Build a structural signature for a comparison so structurally identical
 * siblings can be detected without referential identity.
 *
 * @param {Object} comparison
 * @returns {string}
 */
const comparisonSignature = comparison =>
  [comparison.path, comparison.operator, comparison.value, comparison.valueType ?? ''].join(' ');

// ---------------------------------------------------------------------------
// Analyzer: CONTRADICTION
// ---------------------------------------------------------------------------

/**
 * Within one AND scope, a path forced via `==` to two different constants can
 * never be satisfied. Flag every row that participates in such a conflict.
 * Negated groups are skipped — the logical inversion changes the semantics.
 *
 * @param {Object} group
 * @returns {Array<Object>}
 */
const analyzeContradiction = group => {
  if (group.negated) {
    return [];
  }

  if (group.op !== 'AND') {
    return [];
  }

  const forced = flattenAndComparisons(group).filter(
    comparison =>
      comparison.operator === EQUALITY_OPERATOR &&
      !_.isNil(comparison.path) &&
      comparison.path !== '',
  );

  const byPath = _.groupBy(forced, comparison => comparison.path);

  return Object.values(byPath).flatMap(rows => {
    const distinctValues = _.uniq(rows.map(row => String(row.value)));

    if (distinctValues.length < 2) {
      return [];
    }

    return rows.map(row =>
      makeFinding({
        nodeId: row.id,
        severity: LINT_SEVERITY.ERROR,
        code: LINT_CODE.CONTRADICTION,
        message: `"${row.path}" is forced to conflicting values — this rule can never pass`,
        detail: `Under one AND, "${row.path}" must equal both ${distinctValues
          .map(value => `'${value}'`)
          .join(' and ')}. No record satisfies all of them, so the rule always evaluates false.`,
      }),
    );
  });
};

// ---------------------------------------------------------------------------
// Analyzer: DUPLICATE
// ---------------------------------------------------------------------------

/**
 * Structurally identical sibling comparisons in the same group are redundant.
 * Flag every duplicate occurrence after the first.
 *
 * @param {Object} group
 * @returns {Array<Object>}
 */
const analyzeDuplicate = group => {
  const siblings = (group.children ?? []).filter(child => child.kind === NODE_KIND.COMPARISON);
  const seen = new Set();

  return siblings.reduce((acc, comparison) => {
    const signature = comparisonSignature(comparison);

    if (seen.has(signature)) {
      return [
        ...acc,
        makeFinding({
          nodeId: comparison.id,
          severity: LINT_SEVERITY.WARNING,
          code: LINT_CODE.DUPLICATE,
          message: `Duplicate condition: "${comparison.path}" ${comparison.operator} ${comparison.value}`,
          detail:
            'A structurally identical condition already exists in this group. The copy adds nothing and likely signals an unfinished copy-paste edit.',
        }),
      ];
    }

    seen.add(signature);

    return acc;
  }, []);
};

// ---------------------------------------------------------------------------
// Analyzer: ALWAYS_TRUE / ALWAYS_FALSE
// ---------------------------------------------------------------------------

/**
 * A group with no effective conditions and a self-comparing equality both
 * collapse to a constant truth value. Flag those tautologies/contradictions.
 * Negated groups are skipped — their truth values are inverted, so the
 * existing ALWAYS_TRUE/ALWAYS_FALSE labels would be misleading.
 *
 * @param {Object} group
 * @param {boolean} isRoot
 * @returns {Array<Object>}
 */
const analyzeAlwaysGroup = (group, isRoot) => {
  if (group.negated) {
    return [];
  }

  const meaningfulChildren = (group.children ?? []).filter(
    child => !(child.kind === NODE_KIND.RAW && _.isEmpty(child.text)),
  );

  if (!isRoot && _.isEmpty(meaningfulChildren)) {
    return [
      makeFinding({
        nodeId: group.id,
        severity: LINT_SEVERITY.WARNING,
        code: group.op === 'OR' ? LINT_CODE.ALWAYS_FALSE : LINT_CODE.ALWAYS_TRUE,
        message: 'Empty group has no effect on the rule',
        detail:
          'This group contains no conditions. An empty AND is always true and an empty OR is always false — either way it is dead weight that should be removed.',
      }),
    ];
  }

  return [];
};

/**
 * `x == x` (same path on both sides via value === path) is always true and
 * `x != x` is always false. Comparisons in this model store a literal value,
 * so this fires only when the literal value text equals the path text.
 *
 * @param {Object} comparison
 * @returns {Array<Object>}
 */
const analyzeAlwaysComparison = comparison => {
  if (_.isEmpty(comparison.path) || comparison.path !== String(comparison.value)) {
    return [];
  }

  if (comparison.operator === EQUALITY_OPERATOR) {
    return [
      makeFinding({
        nodeId: comparison.id,
        severity: LINT_SEVERITY.WARNING,
        code: LINT_CODE.ALWAYS_TRUE,
        message: `"${comparison.path}" is compared to itself — always true`,
        detail: 'A field equals itself for every record, so this condition never narrows anything.',
      }),
    ];
  }

  if (comparison.operator === '!=') {
    return [
      makeFinding({
        nodeId: comparison.id,
        severity: LINT_SEVERITY.WARNING,
        code: LINT_CODE.ALWAYS_FALSE,
        message: `"${comparison.path}" is compared to itself — always false`,
        detail: 'A field never differs from itself, so this condition can never pass.',
      }),
    ];
  }

  return [];
};

// ---------------------------------------------------------------------------
// Analyzer: SUSPICIOUS_LITERAL
// ---------------------------------------------------------------------------

/**
 * String literal values that look like debugging leftovers (test, asdf, …) are
 * surfaced as an informational notice.
 *
 * @param {Object} comparison
 * @returns {Array<Object>}
 */
const analyzeSuspiciousLiteral = comparison => {
  const { value } = comparison;

  if (!_.isString(value) || !SUSPICIOUS_LITERAL_RE.test(value)) {
    return [];
  }

  return [
    makeFinding({
      nodeId: comparison.id,
      severity: LINT_SEVERITY.INFO,
      code: LINT_CODE.SUSPICIOUS_LITERAL,
      message: `"${value}" looks like a test value`,
      detail: `The literal "${value}" matches a common debugging-leftover pattern. Confirm it is a real value and not something left in by mistake.`,
    }),
  ];
};

// ---------------------------------------------------------------------------
// Analyzer: INVALID_COLLECTION_ARG
// ---------------------------------------------------------------------------

const analyzeInvalidCollectionArg = collection => {
  const { arrayPath } = collection;

  if (_.isEmpty(arrayPath) || FIELD_PATH_RE.test(arrayPath)) {
    return [];
  }

  const fn = collection.fn ?? 'ANY';

  return [
    makeFinding({
      nodeId: collection.id,
      severity: LINT_SEVERITY.ERROR,
      code: LINT_CODE.INVALID_COLLECTION_ARG,
      message: `${fn}(…) first argument must be a collection field`,
      detail: `${fn} iterates over a collection, so its first argument must be a field path pointing to one (e.g. ${fn}(loan.borrowers, _b => _b.income > 0)). Use OR/AND instead when combining boolean conditions on the same value.`,
    }),
  ];
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Run all static analyzers over a visual builder model tree and return a flat
 * list of findings. Pure and deterministic — needs no schema or runtime data.
 *
 * @param {Object|null|undefined} model
 * @returns {Array<{ id: string, nodeId: string|null, severity: string, code: string, message: string, detail: string }>}
 */
export const lintModel = model => {
  if (_.isNil(model)) {
    return [];
  }

  const groups = collectGroups(model);
  const comparisons = collectComparisons(model);
  const collections = collectCollections(model);

  const groupFindings = groups.flatMap(group => [
    ...analyzeContradiction(group),
    ...analyzeDuplicate(group),
    ...analyzeAlwaysGroup(group, group.id === model.id),
  ]);

  const comparisonFindings = comparisons.flatMap(comparison => [
    ...analyzeAlwaysComparison(comparison),
    ...analyzeSuspiciousLiteral(comparison),
  ]);

  const collectionFindings = collections.flatMap(analyzeInvalidCollectionArg);

  // A comparison nested in an AND is reachable from both its own group scope and
  // every ancestor AND scope (flattenAndComparisons descends nested ANDs), so
  // the same analyzer can flag one nodeId more than once. Collapse to one
  // finding per (code, nodeId).
  return _.uniqBy(
    [...groupFindings, ...comparisonFindings, ...collectionFindings],
    finding => `${finding.code}:${finding.nodeId}`,
  );
};
